import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, CheckSquare, Frame, Receipt, BellRing, BarChart3 } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { Brand, LangToggle, LOGO_STACKED, btnPrimary } from "@/components/Brand";
import { GridSelectionDemo } from "@/components/GridSelectionDemo";
import { ThreeStepFlow } from "@/components/ThreeStepFlow";
import { SiteFooter } from "@/components/SiteFooter";
import { SorterShowcase } from "@/components/SorterShowcase";
import { SupportButton } from "@/components/SupportButton";
import { PlanPrice } from "@/components/PlanPrice";
import { EarlyAccessBadge } from "@/components/EarlyAccessBadge";

const HERO =
  "https://images.unsplash.com/photo-1762843353166-e0542bba1a66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBlZGl0b3JpYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODc1NDk4MDV8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [earlyAccess, setEarlyAccess] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
    api.get("/config").then((r) => setEarlyAccess(r.data.early_access)).catch(() => {});
  }, []);

  const steps = [
    { icon: Camera, title: t("lp_s1_t"), body: t("lp_s1_b") },
    { icon: CheckSquare, title: t("lp_s2_t"), body: t("lp_s2_b") },
    { icon: Frame, title: t("lp_s3_t"), body: t("lp_s3_b") },
    { icon: Receipt, title: t("lp_s4_t"), body: t("lp_s4_b") },
    { icon: BellRing, title: t("lp_s5_t"), body: t("lp_s5_b") },
    { icon: BarChart3, title: t("lp_s6_t"), body: t("lp_s6_b") },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="landing-page">
      <header className="glass fixed top-0 inset-x-0 z-40 border-b border-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 h-16 flex items-center justify-between gap-4">
          <Brand />
          <div className="flex items-center gap-3">
            <LangToggle />
            <Link
              data-testid="client-login-link"
              to="/client-login"
              className="hidden sm:inline-flex items-center min-h-[44px] text-[11px] tracking-[0.18em] uppercase text-[#A8A8A8] hover:text-[#D4AF37] transition-colors"
            >
              {t("nav_client")}
            </Link>
            <Link
              data-testid="header-login-link"
              to="/login"
              className="text-[11px] tracking-[0.18em] uppercase border border-[#2A2A2A] px-4 py-2.5 hover:border-[#D4AF37] transition-colors"
            >
              {t("nav_login")}
            </Link>
          </div>
        </div>
      </header>

      <section className="relative min-h-[94vh] flex items-end overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/75 to-[#050505]/40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-12 pb-20 lg:pb-28 w-full">
          <img src={LOGO_STACKED} alt="Seleksa" className="h-28 sm:h-36 w-auto object-contain mb-8 fade-up" />
          <p className="eyebrow mb-5 fade-up">{t("hero_eyebrow")}</p>
          {Number(earlyAccess?.slots_left || 0) > 0 && (
            <div className="mb-6 fade-up" data-testid="fomo-badge">
              <span className="inline-flex items-center gap-2 border border-[#D4AF37]/60 bg-[#D4AF37]/[0.08] px-4 py-2 font-mono-accent text-[10px] sm:text-[11px] tracking-[0.16em] uppercase text-[#D4AF37]">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" />
                {t("hero_early_access", { left: earlyAccess.slots_left, total: earlyAccess.slots_total })}
              </span>
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium max-w-3xl leading-[1.05] fade-up">
            {t("hero_title_a")}
            <br />
            <span className="text-[#D4AF37] italic">{t("hero_title_b")}</span>
          </h1>
          <p className="mt-7 text-base sm:text-lg text-[#A8A8A8] max-w-xl leading-relaxed fade-up">{t("hero_body")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up">
            <Link data-testid="hero-cta-btn" to="/login" className={`${btnPrimary} px-8 py-4 group`}>
              {t("hero_cta")}
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <span className="text-[11px] tracking-[0.14em] uppercase text-[#707070]">{t("trial_note")}</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-12 py-24 lg:py-32 border-b border-[#1E1E1E]">
        <GridSelectionDemo />
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-12 py-24 lg:py-32 border-b border-[#1E1E1E]">
        <ThreeStepFlow />
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-12 py-24 lg:py-32">
        <p className="eyebrow mb-10">{t("how_it_runs")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`lp_s${i + 1}`} className="bg-[#0B0B0B] p-8 lg:p-10 hover:bg-[#121212] transition-colors duration-500">
              <span className="font-mono-accent text-xs text-[#D4AF37]">0{i + 1}</span>
              <s.icon size={22} strokeWidth={1.25} className="mt-8 text-[#F3F3F1]" />
              <h3 className="mt-6 text-xl tracking-tight">{s.title}</h3>
              <p className="mt-3 text-sm text-[#A8A8A8] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-12 py-24 lg:py-32 border-b border-[#1E1E1E]">
        <SorterShowcase />
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-12 pb-28" data-testid="landing-plans">
        <p className="eyebrow mb-4">{t("pricing")}</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-xl">{t("pricing_body")}</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div key={p.plan_id} className="border border-[#2A2A2A] bg-[#0F0F0F] p-8 flex flex-col">
              <h3 className="text-2xl tracking-tight">{p.name}</h3>
              <div className="mt-4">
                <PlanPrice plan={p} testId={`landing-price-${p.plan_id}`} />
                <EarlyAccessBadge earlyAccess={earlyAccess} testId={`landing-slots-${p.plan_id}`} />
              </div>
              <ul className="mt-7 space-y-2 flex-1">
                {(p.features || []).map((f) => (
                  <li key={f} className="text-sm text-[#A8A8A8] flex gap-2">
                    <span className="text-[#D4AF37]">·</span> {f}
                  </li>
                ))}
              </ul>
              <Link to="/login" className={`${btnPrimary} mt-8`}>
                {t("choose_plan")}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-12 pb-28">
        <div className="border border-[#D4AF37]/30 bg-[#0B0B0B] p-10 lg:p-16 text-center" data-testid="final-cta">
          <p className="eyebrow mb-5">{t("hero_eyebrow")}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium max-w-2xl mx-auto leading-[1.08]">
            {t("demo_title")}
          </h2>
          <Link data-testid="final-cta-btn" to="/login" className={`${btnPrimary} mt-10 px-9 py-4`}>
            {t("hero_cta")} <ArrowRight size={15} />
          </Link>
          <p className="mt-5 text-[11px] tracking-[0.14em] uppercase text-[#707070]">{t("trial_note")}</p>
        </div>
      </section>

      <SiteFooter />
      <SupportButton />
    </div>
  );
}
